"use client";

/**
 * SignatureInput
 * Renders a signature field with Type (cursive font) and Draw (canvas) tabs.
 * Supports touch, mouse, and pen input.
 */

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchTabBar } from "@/fetch-ui/FetchTabBar";
import { useToast } from "@/components/providers/FetchToastProvider";
import type { SignatureValue } from "@/features/forms/_logic/types/file-mapping-types";
import {
  SIGNATURE_FONT_FAMILY,
  SIGNATURE_FONT_SIZE,
  SIGNATURE_STROKE_COLOR,
  SIGNATURE_STROKE_WIDTH,
  SIGNATURE_UPLOAD_DEBOUNCE_MS,
} from "@/features/forms/_logic/constants/file-constants";
import { useUser } from "@/hooks/useUser";
import { uploadFormFile } from "@/lib/database/client/database";
import { createClient } from "@/utils/supabase/client";
import { Eraser } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface SignatureInputProps {
  /** Current signature value (typed text or storage path). */
  value?: SignatureValue;
  /** Called when signature changes. */
  onChange: (value: SignatureValue | null) => void;
  /** Whether the field is required (for form validation). */
  required?: boolean;
  /** Form ID for file uploads. */
  formId: string;
  /** Field ID for file uploads. */
  fieldId: string;
  /** Whether the input is disabled. */
  disabled?: boolean;
}

type SignatureMode = "type" | "draw";

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Signature input with Type (cursive font) and Draw (canvas) modes.
 *
 * @param props - Component props
 * @returns Signature input element
 */
export function SignatureInput({
  value,
  onChange,
  required: _required = false,
  formId,
  fieldId,
  disabled = false,
}: SignatureInputProps) {
  // Note: required is accepted for API compatibility but validation is handled by parent
  // Hooks
  const supabase = useMemo(() => createClient(), []);
  const { user } = useUser();
  const { showToast } = useToast();

  // State
  const [mode, setMode] = useState<SignatureMode>(value?.kind || "type");
  const [typedText, setTypedText] = useState(value?.kind === "type" ? value.value : "");
  const [isDrawing, setIsDrawing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasDrawnContent, setHasDrawnContent] = useState(false);

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Bumped whenever the canvas is cleared or the user switches modes; in-flight
  // uploads compare against the generation at start and drop their result if a
  // newer clear/switch happened while they were running. Prevents the canvas
  // upload from racing past a synchronous clear and reintroducing a stale value.
  const uploadGenerationRef = useRef(0);
  // Captured on pointerdown so per-move pointer math doesn't have to call
  // getBoundingClientRect (which forces a synchronous layout) on every event.
  const strokeRectRef = useRef<DOMRect | null>(null);
  // Mirrors hasDrawnContent for the hot path so pointermove doesn't have to
  // call setHasDrawnContent on every event. State is updated once on pointerup.
  const hasDrawnContentRef = useRef(false);

  // ── Canvas Setup ─────────────────────────────────────────────────────────

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    ctx.strokeStyle = SIGNATURE_STROKE_COLOR;
    ctx.lineWidth = SIGNATURE_STROKE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    return { canvas, ctx };
  }, []);

  useEffect(() => {
    if (mode === "draw") {
      initCanvas();
    }
  }, [mode, initCanvas]);

  // ── Drawing Handlers ─────────────────────────────────────────────────────

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      // Capture pointer for smooth drawing
      canvas.setPointerCapture(e.pointerId);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Cache the bounding rect for the lifetime of the stroke so handlePointerMove
      // doesn't force layout on every event.
      const rect = canvas.getBoundingClientRect();
      strokeRectRef.current = rect;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setIsDrawing(true);
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    [disabled]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing || disabled) return;

      const canvas = canvasRef.current;
      const rect = strokeRectRef.current;
      if (!canvas || !rect) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      ctx.lineTo(x, y);
      ctx.stroke();
      hasDrawnContentRef.current = true;
    },
    [isDrawing, disabled]
  );

  // ── Upload Logic ─────────────────────────────────────────────────────────

  const uploadSignature = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Skip upload if no drawn content (uses state instead of pixel scan)
    if (!hasDrawnContent) {
      onChange(null);
      return;
    }

    const generationAtStart = uploadGenerationRef.current;

    try {
      setIsUploading(true);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to create blob"));
        }, "image/png");
      });

      const file = new File([blob], "signature.png", { type: "image/png" });
      const numericFormId = Number(formId);
      if (!Number.isInteger(numericFormId) || numericFormId < 1) {
        console.error("uploadSignature: invalid formId", { formId });
        showToast("Failed to save signature", "error");
        return;
      }
      const filePath = await uploadFormFile(supabase, numericFormId, user?.user_id || null, fieldId, file);

      // Discard result if the user cleared or switched modes while we were uploading.
      if (uploadGenerationRef.current !== generationAtStart) return;

      if (filePath) {
        onChange({ kind: "draw", value: filePath });
        showToast("Signature saved", "success");
      } else {
        showToast("Failed to save signature", "error");
      }
    } catch (error) {
      console.error("Error uploading signature:", error);
      if (uploadGenerationRef.current === generationAtStart) {
        showToast("Failed to save signature", "error");
      }
    } finally {
      setIsUploading(false);
    }
  }, [supabase, formId, fieldId, user, onChange, showToast, hasDrawnContent]);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;

      const canvas = canvasRef.current;
      if (canvas) {
        canvas.releasePointerCapture(e.pointerId);
      }

      setIsDrawing(false);
      strokeRectRef.current = null;

      // Commit the per-stroke ref to state once so the render observes the change.
      if (!hasDrawnContentRef.current) return;
      if (!hasDrawnContent) setHasDrawnContent(true);

      // Debounced upload
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }

      uploadTimeoutRef.current = setTimeout(() => {
        uploadSignature();
      }, SIGNATURE_UPLOAD_DEBOUNCE_MS);
    },
    [isDrawing, hasDrawnContent, uploadSignature]
  );

  // ── Clear Handler ────────────────────────────────────────────────────────

  const clearSignature = useCallback(() => {
    // Invalidate any pending upload so its onChange doesn't overwrite null.
    uploadGenerationRef.current += 1;
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }

    if (mode === "type") {
      setTypedText("");
      onChange(null);
    } else {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      hasDrawnContentRef.current = false;
      strokeRectRef.current = null;
      setHasDrawnContent(false);
      onChange(null);
    }
  }, [mode, onChange]);

  // ── Type Mode Handler ────────────────────────────────────────────────────

  const handleTypedTextChange = useCallback(
    (text: string) => {
      setTypedText(text);
      if (text.trim()) {
        onChange({ kind: "type", value: text });
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  // ── Mode Switch Handler ──────────────────────────────────────────────────

  const handleModeChange = useCallback(
    (newMode: SignatureMode) => {
      if (newMode === mode) return;

      // Switching modes discards the previous mode's in-progress input.
      // clearSignature bumps the upload generation so any pending pointer-up
      // debounce or in-flight upload drops its result instead of racing past
      // the clear and re-committing a stale value.
      clearSignature();
      setMode(newMode);
    },
    [mode, clearSignature]
  );

  // ── Cleanup ──────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
    };
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-3">
      {/* Mode Tabs */}
      <FetchTabBar
        size="sm"
        variant="slider"
        layoutId={`signature-mode-${fieldId}`}
        activeTab={mode}
        onTabChange={(tabId) => handleModeChange(tabId as SignatureMode)}
        tabs={[
          { id: "type", label: "Type", disabled },
          { id: "draw", label: "Draw", disabled },
        ]}
      />

      {/* Type Mode */}
      {mode === "type" && (
        <div className="flex flex-col gap-2">
          <FetchInput
            type="text"
            value={typedText}
            onChange={(e) => handleTypedTextChange(e.target.value)}
            placeholder="Type your name"
            disabled={disabled}
          />
          {typedText && (
            <div
              className="h-16 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-lg"
              style={{
                fontFamily: `'${SIGNATURE_FONT_FAMILY}', cursive`,
                fontSize: `${SIGNATURE_FONT_SIZE}px`,
              }}
            >
              {typedText}
            </div>
          )}
        </div>
      )}

      {/* Draw Mode */}
      {mode === "draw" && (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerUp}
              aria-label="Signature drawing area. Use a pointer device to draw, or switch to Type mode for keyboard entry."
              role="img"
              className={`w-full h-32 border border-gray-300 rounded-lg bg-white touch-none ${
                disabled ? "cursor-not-allowed opacity-50" : "cursor-crosshair"
              }`}
              style={{ touchAction: "none" }}
            />
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                <span className="text-sm text-gray-600">Saving...</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">{hasDrawnContent ? "Lift to save" : "Draw your signature above"}</p>
        </div>
      )}

      {/* Clear Button */}
      {(typedText || hasDrawnContent || value) && !disabled && (
        <div className="flex justify-end">
          <FetchButton variant="ghost" size="sm" onClick={clearSignature} leftIcon={<Eraser className="w-4 h-4" />}>
            Clear
          </FetchButton>
        </div>
      )}
    </div>
  );
}

export default SignatureInput;
